/**
 * VideoConfigFlyout — Config flyout content for the video node control bar.
 *
 * Five control groups: Model, Aspect Ratio, Resolution, Duration, Generate Audio.
 * Resolution and Duration rendering are model-dependent via getVideoConstraints().
 * All changes persist immediately via onChange callbacks.
 */

import { memo, useEffect, useMemo, useState } from 'react'
import { listVoiceProviders, type VoiceProviderSummary } from '@ponpon/api-client'
import { getVideoConstraints } from './videoConstraints'
import { getVideoModelLabel, getTtsProviderLabel } from '../../../../constants/modelAliases'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VideoConfigFlyoutProps {
  model: string
  onModelChange: (model: string) => void
  aspectRatio: string
  onAspectRatioChange: (ratio: string) => void
  resolution: string
  onResolutionChange: (resolution: string) => void
  duration: number
  onDurationChange: (duration: number) => void
  generateAudio: boolean
  onGenerateAudioChange: (generateAudio: boolean) => void
  ttsProvider: string
  onTtsProviderChange: (provider: string) => void
  /** Controls how dialogue is delivered: 'tts_remix' or 'seedance_inline'. */
  dialogueMode?: string
  onDialogueModeChange?: (mode: string) => void
}

const DIALOGUE_MODE_OPTIONS: Array<{ value: string; label: string; hint: string }> = [
  {
    value: 'tts_remix',
    label: 'TTS remix',
    hint: 'ElevenLabs voice + audio remix. Voice IDs stay locked across clips.',
  },
  {
    value: 'seedance_inline',
    label: 'Seedance inline',
    hint: 'Seedance reads dialogue off a caption-band frame. Lip sync exact; voice timbre best-effort.',
  },
]

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODEL_OPTIONS = [
  'seedance-2.0-image-to-video',
  'seedance-2.0-reference-to-video',
  'kling-v3-pro',
  'veo-3.1',
  // Self-hosted H3. The alias table already labelled this "Lyra H3" — it was
  // simply never offered here, which is what kept VIDEO_MODEL_UI_CHOICES
  // narrow enough to reject it at the Director boundary.
  'minimax-h3',
].map((value) => ({ value, label: getVideoModelLabel(value) }))

const ASPECT_RATIO_OPTIONS = ['16:9', '9:16']

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-1.5'

function segmentedBtnClass(isSelected: boolean): string {
  return `flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
    isSelected
      ? 'bg-primary/20 text-primary border border-primary/40'
      : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
  }`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const VideoConfigFlyout = memo(function VideoConfigFlyout({
  model,
  onModelChange,
  aspectRatio,
  onAspectRatioChange,
  resolution,
  onResolutionChange,
  duration,
  onDurationChange,
  generateAudio,
  onGenerateAudioChange,
  ttsProvider,
  onTtsProviderChange,
  dialogueMode = 'tts_remix',
  onDialogueModeChange,
}: VideoConfigFlyoutProps) {
  const inlineMode = dialogueMode === 'seedance_inline'
  const activeDialogueHint =
    DIALOGUE_MODE_OPTIONS.find((opt) => opt.value === dialogueMode)?.hint ?? ''
  const constraints = getVideoConstraints(model, resolution)
  const [voiceProviders, setVoiceProviders] = useState<VoiceProviderSummary[]>([])
  const [providerLoadState, setProviderLoadState] = useState<'loading' | 'loaded' | 'error'>('loading')

  useEffect(() => {
    setProviderLoadState('loading')
    listVoiceProviders()
      .then((providers) => {
        setVoiceProviders(providers)
        setProviderLoadState('loaded')
      })
      .catch(() => setProviderLoadState('error'))
  }, [])

  const providerOptions = useMemo(() => {
    if (voiceProviders.some((provider) => provider.id === ttsProvider)) return voiceProviders
    return [
      ...voiceProviders,
      { id: ttsProvider, label: ttsProvider },
    ]
  }, [ttsProvider, voiceProviders])

  return (
    <div className="flex flex-col gap-3" data-testid="video-config-flyout-content">
      {/* Model */}
      <div>
        <label className={LABEL_CLASS}>Model</label>
        <div className="flex gap-1">
          {MODEL_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onModelChange(opt.value)}
              className={segmentedBtnClass(model === opt.value)}
              data-testid={`model-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Aspect Ratio */}
      <div>
        <label className={LABEL_CLASS}>Aspect Ratio</label>
        <div className="flex gap-1">
          {ASPECT_RATIO_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => onAspectRatioChange(opt)}
              className={segmentedBtnClass(aspectRatio === opt)}
              data-testid={`aspect-ratio-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Resolution (model-dependent) */}
      <div>
        <label className={LABEL_CLASS}>Resolution</label>
        {constraints.resolutionOptions ? (
          <div className="flex gap-1">
            {constraints.resolutionOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => onResolutionChange(opt)}
                className={segmentedBtnClass(resolution === opt)}
                data-testid={`resolution-${opt}`}
              >
                {opt === '4k' ? '4K' : opt}
              </button>
            ))}
          </div>
        ) : (
          <span
            className="text-xs text-text-secondary"
            data-testid="resolution-static"
          >
            {constraints.resolutionLabel ?? 'Auto'}
          </span>
        )}
      </div>

      {/* Duration (model-dependent) */}
      <div>
        <label className={LABEL_CLASS}>Duration</label>
        {constraints.durationOptions ? (
          <div className="flex flex-wrap gap-1">
            {constraints.durationOptions.map((d) => (
              <button
                key={d}
                onClick={() => onDurationChange(d)}
                className={segmentedBtnClass(duration === d)}
                data-testid={`duration-${d}s`}
              >
                {d}s
              </button>
            ))}
          </div>
        ) : (
          <span
            className="text-xs text-text-secondary"
            data-testid="duration-static"
          >
            {constraints.durationDefault}s
          </span>
        )}
      </div>

      {/* Generate Audio */}
      <div>
        <label className={LABEL_CLASS}>Generate Audio</label>
        <div className="flex gap-1">
          <button
            onClick={() => onGenerateAudioChange(true)}
            className={segmentedBtnClass(generateAudio === true)}
            data-testid="generate-audio-on"
          >
            On
          </button>
          <button
            onClick={() => onGenerateAudioChange(false)}
            className={segmentedBtnClass(generateAudio === false)}
            data-testid="generate-audio-off"
          >
            Off
          </button>
        </div>
      </div>

      {/* Dialogue Mode */}
      {onDialogueModeChange && (
        <div>
          <label className={LABEL_CLASS}>Dialogue Mode</label>
          <div className="flex gap-1">
            {DIALOGUE_MODE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onDialogueModeChange(opt.value)}
                className={segmentedBtnClass(dialogueMode === opt.value)}
                data-testid={`dialogue-mode-${opt.value}`}
                title={opt.hint}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {activeDialogueHint && (
            <p className="mt-1 text-xxs text-text-muted leading-snug">
              {activeDialogueHint}
            </p>
          )}
        </div>
      )}

      {generateAudio && !inlineMode && (
        <div>
          <label className={LABEL_CLASS}>TTS Provider</label>
          {providerLoadState === 'loading' && (
            <div className="rounded-md border border-border-subtle bg-surface-2 px-2 py-1.5 text-xs text-text-muted">
              Loading providers...
            </div>
          )}
          {providerLoadState === 'error' && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
              Provider catalog unavailable
            </div>
          )}
          {providerLoadState === 'loaded' && (
            <div className="flex gap-1">
              {providerOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => onTtsProviderChange(opt.id)}
                  className={segmentedBtnClass(ttsProvider === opt.id)}
                  data-testid={`tts-provider-${opt.id}`}
                >
                  {getTtsProviderLabel(opt.id)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
})

VideoConfigFlyout.displayName = 'VideoConfigFlyout'
